import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { MedicalHistoryDocumentType } from "@/src/models/constants/document-types";
import {
  COMPANY_NAME_OTHER_PROMPT,
  DOCUMENT_TYPE_PROMPT,
  HOME_HEALTH_COMPANY_NAME_PROMPT,
  HOSPITAL_ABBREV_PROMPT,
} from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { Diagnosis } from "@/src/models/documents/referral";
import { locationSchema } from "@/src/models/location";
import { patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const medicalHistoryTypeDisplay: Record<MedicalHistoryDocumentType, string> = {
  [MedicalHistoryDocumentType.CHART_FROM_CLINIC]: "Chart from Clinic",
  [MedicalHistoryDocumentType.CHART_FROM_HOSPITAL]: "Chart from Hospital",
  [MedicalHistoryDocumentType.COVID]: "Covid",
  [MedicalHistoryDocumentType.HOME_HEALTH]: "Home Health",
  [MedicalHistoryDocumentType.HOSPICE]: "Hospice",
  [MedicalHistoryDocumentType.IMMUNIZATION]: "Immunization/Vax Records",
  [MedicalHistoryDocumentType.MEDICAL_HISTORY]: "Medical History",
  [MedicalHistoryDocumentType.MEDICAL_HISTORY_MEDICATIONS]: "Medications",
  [MedicalHistoryDocumentType.NUTRITION]: "Nutrition",
  [MedicalHistoryDocumentType.RESPONSE]: "Medical History Response",
};

export const medicalHistoryTypeSchema = z.nativeEnum(MedicalHistoryDocumentType);
export enum medicalHistoryType {
  ORDER = "Order",
  REPORT = "Report",
  CONSULT = "Consult",
  POC = "POC",
  MISSED_MED = "Missed Med",
  MED_REQUEST = "Med Request",
}

const baseMedicalHistoryFields = {
  patient: patientSchema,
};

const extendedMedicalHistorySchema = z.object({
  isAdw: isAdwSchema,
  isSigned: z.boolean().nullable(),
  ...DocumentDateSchema.shape,
  availableForDownload: z.boolean().nullable(),
  orderNumber: z.string().nullable(),
  clinicName: z.string().nullable(),
  companyName: z.string().nullable().describe(HOME_HEALTH_COMPANY_NAME_PROMPT),
  companyNameOther: z.string().nullable().describe(COMPANY_NAME_OTHER_PROMPT),
  documentType: z.string().nullable().describe(DOCUMENT_TYPE_PROMPT),
  location: locationSchema.nullable(),
  medicalHistoryType: z.nativeEnum(medicalHistoryType).nullable(),
  vaccineType: z.string().nullable(),
  hospitalAbbrev: z.string().nullable().describe(HOSPITAL_ABBREV_PROMPT),
  signatureRequired: z.boolean().nullable(),
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
});

const medicalHistoryCustomFields: Record<MedicalHistoryDocumentType, Record<string, z.ZodTypeAny>> = {
  [MedicalHistoryDocumentType.CHART_FROM_CLINIC]: extendedMedicalHistorySchema.shape,
  [MedicalHistoryDocumentType.CHART_FROM_HOSPITAL]: extendedMedicalHistorySchema.shape,
  [MedicalHistoryDocumentType.COVID]: extendedMedicalHistorySchema.pick({ dateOfDocument: true }).shape,
  [MedicalHistoryDocumentType.HOME_HEALTH]: extendedMedicalHistorySchema.pick({
    dateOfDocument: true,
    dateOfEvent: true,
    documentType: true,
    orderNumber: true,
    location: true,
    companyName: true,
    companyNameOther: true,
    medicalHistoryType: true,
    signatureRequired: true,
    provider: true,
    receivingProvider: true,
  }).shape,

  [MedicalHistoryDocumentType.HOSPICE]: extendedMedicalHistorySchema.pick({
    dateOfDocument: true,
    orderNumber: true,
    location: true,
    companyName: true,
    medicalHistoryType: true,
    signatureRequired: true,
  }).shape,
  [MedicalHistoryDocumentType.IMMUNIZATION]: extendedMedicalHistorySchema.pick({
    isAdw: true,
    isSigned: true,
    dateOfEvent: true,
    dateReceived: true,
    vaccineType: true,
  }).shape,
  [MedicalHistoryDocumentType.NUTRITION]: extendedMedicalHistorySchema.pick({ dateOfEvent: true }).shape,
  [MedicalHistoryDocumentType.MEDICAL_HISTORY]: extendedMedicalHistorySchema.shape,
  [MedicalHistoryDocumentType.MEDICAL_HISTORY_MEDICATIONS]: extendedMedicalHistorySchema.shape,
  [MedicalHistoryDocumentType.RESPONSE]: extendedMedicalHistorySchema.pick({
    dateReceived: true,
    availableForDownload: true,
  }).shape,
};

const makeMedicalHistorySchema = (type: MedicalHistoryDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseMedicalHistoryFields,
    ...customFields,
  });

export const medicalHistorySchemas: Record<MedicalHistoryDocumentType, ReturnType<typeof makeMedicalHistorySchema>> = (
  Object.values(MedicalHistoryDocumentType) as MedicalHistoryDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeMedicalHistorySchema(type, medicalHistoryCustomFields[type]);

    return acc;
  },
  {} as Record<MedicalHistoryDocumentType, ReturnType<typeof makeMedicalHistorySchema>>
);

const medicalHistorySchemasArray = (Object.values(MedicalHistoryDocumentType) as MedicalHistoryDocumentType[]).map(
  (type) => medicalHistorySchemas[type]
) as [ReturnType<typeof makeMedicalHistorySchema>, ...ReturnType<typeof makeMedicalHistorySchema>[]];

export const anyMedicalHistorySchema = z.discriminatedUnion("type", medicalHistorySchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type MedicalHistory = ExtractedDocumentFields<
  MedicalHistoryDocumentType,
  typeof extendedMedicalHistorySchema,
  typeof baseMedicalHistoryFields
> &
  DatabaseDefaults & {
    /**
     * Written flat into `section.reviewedContent` by the DOCUMENT_INFORMATION block for any
     * classification that enables `documentInformation.diagnosisList` (e.g. usrc's
     * MEDICAL_HISTORY_CHART_FROM_CLINIC / _FROM_HOSPITAL), so a reviewed medical-history section
     * can carry a diagnosis list even though no medical-history extraction schema declares one.
     * Optional because most classifications leave the field disabled.
     */
    diagnosisList?: Diagnosis[] | null;
  };

const now = new Date().toISOString();

export const medicalHistoryShape: MedicalHistory = {
  id: "",
  type: MedicalHistoryDocumentType.MEDICAL_HISTORY,
  patient: null,
  orderNumber: null,
  clinicName: null,
  companyName: null,
  companyNameOther: null,
  documentType: null,
  location: null,
  medicalHistoryType: null,
  vaccineType: null,
  hospitalAbbrev: null,
  signatureRequired: null,
  isSigned: null,
  dateOfEvent: null,
  dateReceived: null,
  dateOfDocument: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
